import { useState, useEffect, useCallback, useRef } from "react";
import { authClient } from "@/lib/auth-client";
import { Button, ErrorBlock } from "@/components/Ui";

type User = {
	id: string;
	name: string;
	email: string;
	emailVerified: boolean;
	image: string | null;
	role: string | null;
	banned: boolean | null;
	banReason: string | null;
	banExpires: string | null;
	createdAt: Date | string;
};

type ListResponse = {
	users: User[];
	total: number;
};

const ROLES: ("user" | "admin")[] = ["user", "admin"];
const PAGE_SIZE = 20;

function Badge({
	children,
	color,
}: {
	children: React.ReactNode;
	color: string;
}) {
	return (
		<span
			style={{
				display: "inline-block",
				padding: "0.15rem 0.5rem",
				borderRadius: "9999px",
				fontSize: "0.7rem",
				fontWeight: 600,
				background: color,
				color: "#fff",
			}}
		>
			{children}
		</span>
	);
}

function formatDate(d: Date | string) {
	return new Date(d).toLocaleDateString("id-ID", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

const inputStyle: React.CSSProperties = {
	padding: "0.55rem 0.75rem",
	borderRadius: "var(--radius-md)",
	border: "1px solid var(--color-border)",
	background: "var(--color-bg)",
	color: "var(--color-text)",
	fontSize: "0.85rem",
};

export default function AdminUsers() {
	const [users, setUsers] = useState<User[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [actionLoading, setActionLoading] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [newName, setNewName] = useState("");
	const [newEmail, setNewEmail] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [newRole, setNewRole] = useState("user");

	const fetchUsers = useCallback(
		async (offset = 0, searchVal = "") => {
			setLoading(true);
			setError(null);
			const requestId = ++fetchUsersRef.current;
			try {
				const query: Record<string, string | number> = {
					limit: PAGE_SIZE,
					offset,
				};
				if (searchVal) {
					query.searchValue = searchVal;
					query.searchField = "email";
					query.searchOperator = "contains";
				}
				const res = await authClient.admin.listUsers({ query });
				if (fetchUsersRef.current !== requestId) return;
				if (res.error) {
					setError(res.error.message ?? "Gagal memuat user");
					return;
				}
				const data = res.data as ListResponse;
				setUsers(data.users);
				setTotal(data.total);
			} catch (err) {
				if (fetchUsersRef.current !== requestId) return;
				setError(err instanceof Error ? err.message : "Terjadi kesalahan");
			} finally {
				if (fetchUsersRef.current === requestId) {
					setLoading(false);
				}
			}
		},
		[],
	);
	const fetchUsersRef = useRef(0);

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect
		fetchUsers(0);
	}, [fetchUsers]);

	const totalPages = Math.ceil(total / PAGE_SIZE);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(0);
		fetchUsers(0, search);
	};

	const handleSetRole = async (userId: string, role: "user" | "admin") => {
		setActionLoading(true);
		try {
			const res = await authClient.admin.setRole({ userId, role });
			if (res.error) {
				setError(res.error.message ?? "Gagal mengubah role");
				return;
			}
			setUsers((prev) =>
				prev.map((u) => (u.id === userId ? { ...u, role } : u)),
			);
			if (selectedUser?.id === userId) {
				setSelectedUser((prev) => (prev ? { ...prev, role } : prev));
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Gagal mengubah role");
		} finally {
			setActionLoading(false);
		}
	};

	const handleBan = async (userId: string) => {
		setActionLoading(true);
		try {
			const res = await authClient.admin.banUser({ userId });
			if (res.error) {
				setError(res.error.message ?? "Gagal ban user");
				return;
			}
			setUsers((prev) =>
				prev.map((u) => (u.id === userId ? { ...u, banned: true } : u)),
			);
			if (selectedUser?.id === userId) {
				setSelectedUser((prev) =>
					prev ? { ...prev, banned: true } : prev,
				);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Gagal ban user");
		} finally {
			setActionLoading(false);
		}
	};

	const handleUnban = async (userId: string) => {
		setActionLoading(true);
		try {
			const res = await authClient.admin.unbanUser({ userId });
			if (res.error) {
				setError(res.error.message ?? "Gagal unban user");
				return;
			}
			setUsers((prev) =>
				prev.map((u) =>
					u.id === userId
						? { ...u, banned: false, banReason: null, banExpires: null }
						: u,
				),
			);
			if (selectedUser?.id === userId) {
				setSelectedUser((prev) =>
					prev
						? { ...prev, banned: false, banReason: null, banExpires: null }
						: prev,
				);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Gagal unban user");
		} finally {
			setActionLoading(false);
		}
	};

	const handleCreateUser = async (e: React.FormEvent) => {
		e.preventDefault();
		setActionLoading(true);
		setError(null);
		try {
			const res = await authClient.admin.createUser({
				email: newEmail,
				password: newPassword,
				name: newName,
				role: newRole as "user" | "admin",
			});
			if (res.error) {
				setError(res.error.message ?? "Gagal membuat user");
				return;
			}
			setCreateOpen(false);
			setNewName("");
			setNewEmail("");
			setNewPassword("");
			setNewRole("user");
			fetchUsers(page * PAGE_SIZE, search);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Terjadi kesalahan");
		} finally {
			setActionLoading(false);
		}
	};

	return (
		<div>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: "1rem",
				}}
			>
				<h2 style={{ fontSize: "1rem", fontWeight: 700, margin: 0 }}>
					Users
				</h2>
				<Button
					variant="primary"
					onClick={() => setCreateOpen(!createOpen)}
					style={{ fontSize: "0.82rem", padding: "0.45rem 0.9rem" }}
				>
					{createOpen ? "Tutup" : "+ Buat User"}
				</Button>
			</div>

			{createOpen && (
				<form
					onSubmit={handleCreateUser}
					style={{
						padding: "1rem",
						borderRadius: "var(--radius-md)",
						border: "1px solid var(--color-border)",
						background: "var(--color-surface)",
						marginBottom: "1.25rem",
					}}
				>
					<h3
						style={{
							fontSize: "0.9rem",
							fontWeight: 700,
							margin: "0 0 0.75rem",
						}}
					>
						Buat User Baru
					</h3>
					<div style={{ display: "grid", gap: "0.6rem" }}>
						<input
							placeholder="Nama"
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							required
							style={inputStyle}
						/>
						<input
							placeholder="Email"
							type="email"
							value={newEmail}
							onChange={(e) => setNewEmail(e.target.value)}
							required
							style={inputStyle}
						/>
						<input
							placeholder="Password (min 8 karakter)"
							type="password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							required
							minLength={8}
							style={inputStyle}
						/>
						<select
							value={newRole}
							onChange={(e) => setNewRole(e.target.value)}
							style={inputStyle}
						>
							{ROLES.map((r) => (
								<option key={r} value={r}>
									{r}
								</option>
							))}
						</select>
					</div>
					<div style={{ marginTop: "0.75rem" }}>
						<Button
							type="submit"
							variant="primary"
							disabled={actionLoading}
							style={{ fontSize: "0.82rem", padding: "0.45rem 0.9rem" }}
						>
							{actionLoading ? "..." : "Buat"}
						</Button>
					</div>
				</form>
			)}

			<form
				onSubmit={handleSearch}
				style={{
					display: "flex",
					gap: "0.5rem",
					marginBottom: "1rem",
				}}
			>
				<input
					placeholder="Cari email..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					style={{
						flex: 1,
						padding: "0.5rem 0.75rem",
						borderRadius: "var(--radius-md)",
						border: "1px solid var(--color-border)",
						background: "var(--color-surface)",
						color: "var(--color-text)",
						fontSize: "0.85rem",
					}}
				/>
				<Button
					type="submit"
					variant="secondary"
					style={{ fontSize: "0.82rem", padding: "0.45rem 0.9rem" }}
				>
					Cari
				</Button>
			</form>

			{error && (
				<div style={{ marginBottom: "1rem" }}>
					<ErrorBlock message={error} />
				</div>
			)}

			{loading ? (
				<div
					style={{
						textAlign: "center",
						padding: "2rem",
						color: "var(--color-muted)",
					}}
				>
					Memuat...
				</div>
			) : (
				<div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
					{users.map((u) => (
						<button
							type="button"
							key={u.id}
							onClick={() => setSelectedUser(u)}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "0.75rem",
								padding: "0.75rem 1rem",
								borderRadius: "var(--radius-md)",
								border: "1px solid var(--color-border)",
								background: "var(--color-surface)",
								cursor: "pointer",
								transition: "border-color 150ms",
								width: "100%",
								textAlign: "left",
								font: "inherit",
							}}
							onMouseEnter={(e) =>
								(e.currentTarget.style.borderColor = "var(--color-primary)")
							}
							onMouseLeave={(e) =>
								(e.currentTarget.style.borderColor = "var(--color-border)")
							}
						>
							<div
								style={{
									width: 36,
									height: 36,
									borderRadius: "50%",
									background: "var(--color-primary-soft)",
									color: "var(--color-primary)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									fontWeight: 700,
									fontSize: "0.85rem",
									flexShrink: 0,
								}}
							>
								{u.name.charAt(0).toUpperCase()}
							</div>
							<div style={{ flex: 1, minWidth: 0 }}>
								<div
									style={{
										fontSize: "0.85rem",
										fontWeight: 600,
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{u.name}
								</div>
								<div
									style={{
										fontSize: "0.75rem",
										color: "var(--color-muted)",
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{u.email}
								</div>
							</div>
							<div style={{ display: "flex", gap: "0.35rem", flexShrink: 0 }}>
								{u.role === "admin" && (
									<Badge color="var(--color-primary)">admin</Badge>
								)}
								{u.banned && <Badge color="#dc2626">banned</Badge>}
							</div>
						</button>
					))}
				</div>
			)}

			{totalPages > 1 && (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						gap: "0.5rem",
						marginTop: "1rem",
					}}
				>
					<Button
						variant="secondary"
						disabled={page === 0}
						onClick={() => {
							const p = page - 1;
							setPage(p);
							fetchUsers(p * PAGE_SIZE, search);
						}}
						style={{ fontSize: "0.78rem", padding: "0.35rem 0.7rem" }}
					>
						Sebelumnya
					</Button>
					<span
						style={{
							fontSize: "0.78rem",
							color: "var(--color-muted)",
							display: "flex",
							alignItems: "center",
						}}
					>
						{page + 1} / {totalPages}
					</span>
					<Button
						variant="secondary"
						disabled={page >= totalPages - 1}
						onClick={() => {
							const p = page + 1;
							setPage(p);
							fetchUsers(p * PAGE_SIZE, search);
						}}
						style={{ fontSize: "0.78rem", padding: "0.35rem 0.7rem" }}
					>
						Selanjutnya
					</Button>
				</div>
			)}

			{selectedUser && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 100,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "var(--color-overlay)",
					}}
					onClick={() => setSelectedUser(null)}
				>
					<div
						onClick={(e) => e.stopPropagation()}
						style={{
							width: "100%",
							maxWidth: 380,
							padding: "1.25rem",
							borderRadius: "var(--radius-lg)",
							border: "1px solid var(--color-border)",
							background: "var(--color-surface)",
							margin: "1rem",
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: "1rem",
							}}
						>
							<h3
								style={{
									fontSize: "1rem",
									fontWeight: 700,
									margin: 0,
								}}
							>
								User Details
							</h3>
							<button
								onClick={() => setSelectedUser(null)}
								style={{
									background: "none",
									border: "none",
									color: "var(--color-muted)",
									cursor: "pointer",
									fontSize: "1.2rem",
									padding: "0.25rem",
								}}
							>
								x
							</button>
						</div>

						<div style={{ marginBottom: "1rem" }}>
							<div style={{ fontSize: "0.9rem", fontWeight: 600 }}>
								{selectedUser.name}
							</div>
							<div
								style={{
									fontSize: "0.8rem",
									color: "var(--color-muted)",
								}}
							>
								{selectedUser.email}
							</div>
							<div
								style={{
									fontSize: "0.75rem",
									color: "var(--color-muted)",
									marginTop: "0.25rem",
								}}
							>
								ID: {selectedUser.id}
							</div>
							<div
								style={{
									fontSize: "0.75rem",
									color: "var(--color-muted)",
								}}
							>
								Bergabung: {formatDate(selectedUser.createdAt)}
							</div>
							<div
								style={{
									fontSize: "0.75rem",
									color: "var(--color-muted)",
								}}
							>
								Email verified:{" "}
								{selectedUser.emailVerified ? "Ya" : "Tidak"}
							</div>
							{selectedUser.banned && (
								<div
									style={{
										fontSize: "0.75rem",
										color: "#dc2626",
										marginTop: "0.25rem",
									}}
								>
									Dibanned: {selectedUser.banReason ?? "Tanpa alasan"}
								</div>
							)}
						</div>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "0.5rem",
							}}
						>
							<div>
								<div
									style={{
										fontSize: "0.75rem",
										fontWeight: 600,
										marginBottom: "0.35rem",
										color: "var(--color-muted)",
									}}
								>
									Role
								</div>
								<div style={{ display: "flex", gap: "0.35rem" }}>
									{ROLES.map((r) => (
										<button
											key={r}
											disabled={
												actionLoading || selectedUser.role === r
											}
											onClick={() => handleSetRole(selectedUser.id, r)}
											style={{
												padding: "0.3rem 0.65rem",
												borderRadius: "var(--radius-md)",
												border: `1px solid ${
													selectedUser.role === r
														? "var(--color-primary)"
														: "var(--color-border)"
												}`,
												background:
													selectedUser.role === r
														? "var(--color-primary)"
														: "transparent",
												color:
													selectedUser.role === r
														? "#fff"
														: "var(--color-text)",
												fontSize: "0.78rem",
												fontWeight: 600,
												cursor:
													selectedUser.role === r
														? "default"
														: "pointer",
												opacity: actionLoading ? 0.5 : 1,
											}}
										>
											{r}
										</button>
									))}
								</div>
							</div>

							<div
								style={{
									display: "flex",
									gap: "0.5rem",
									marginTop: "0.5rem",
								}}
							>
								{selectedUser.banned ? (
									<Button
										variant="secondary"
										disabled={actionLoading}
										onClick={() => handleUnban(selectedUser.id)}
										style={{
											flex: 1,
											fontSize: "0.78rem",
											padding: "0.4rem",
											color: "#16a34a",
										}}
									>
										{actionLoading ? "..." : "Unban"}
									</Button>
								) : (
									<Button
										variant="secondary"
										disabled={
											actionLoading || selectedUser.role === "admin"
										}
										onClick={() => handleBan(selectedUser.id)}
										style={{
											flex: 1,
											fontSize: "0.78rem",
											padding: "0.4rem",
											color: "#dc2626",
											borderColor: "#dc2626",
										}}
									>
										{actionLoading ? "..." : "Ban"}
									</Button>
								)}
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
